import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

createRoot(document.getElementById("root")!).render(<App />);

// Service Worker Registration — Phase 2 rollout.
// SW is enabled when EITHER:
//   (a) the user is logged in with role === 'admin', OR
//   (b) localStorage.pwa-enabled === 'true' (manual override for testing).
// Auth check is one fetch to /api/auth/me on page load. If it fails (network,
// 401, etc.) we fall back to the flag only — never crash, never block boot.
// To force-disable on any account:
//   localStorage.removeItem('pwa-enabled'); location.reload();
//   (admin users still get the SW unless they also clear their session)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    const flagOptIn = localStorage.getItem('pwa-enabled') === 'true';

    let isAdmin = false;
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const user = await res.json();
        isAdmin = user?.role === 'admin';
      }
    } catch {
      // Auth probe failed (offline, network error) — fall back to flag.
    }

    const pwaEnabled = isAdmin || flagOptIn;

    if (!pwaEnabled) {
      // Gate is off — clean up any previously-registered SW so a user who
      // loses admin (or toggles the flag off) doesn't keep a stale SW.
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        await reg.unregister();
      }
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);

      // When a new SW is waiting (a new build is deployed), tell the app so the
      // toast can prompt the user to refresh.
      const notifyUpdate = (sw: ServiceWorker) => {
        // Stash globally so the toast button can post SKIP_WAITING to it.
        (window as any).__pwaWaitingSw = sw;
        window.dispatchEvent(new CustomEvent('pwa-update-available'));
      };

      // Case A: a new SW was already waiting when we registered.
      if (registration.waiting) {
        notifyUpdate(registration.waiting);
      }

      // Case B: a new SW finishes installing while this page is open.
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            // 'installed' + an existing controller = update waiting.
            notifyUpdate(installing);
          }
        });
      });

      // After the user clicks the toast button, SKIP_WAITING is sent and the
      // new SW takes control. Reload so the page is served by the new bundle.
      let reloading = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloading) return;
        reloading = true;
        window.location.reload();
      });
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  });

  // Toast button calls this to activate the waiting SW.
  (window as any).activatePwaUpdate = () => {
    const sw: ServiceWorker | undefined = (window as any).__pwaWaitingSw;
    if (sw) {
      sw.postMessage({ type: 'SKIP_WAITING' });
    }
  };
}

// Install Prompt Logic (Mobile Only)
let deferredPrompt: any = null;

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
  
  // Check if mobile device
  const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
  
  // Check if already installed
  const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
  
  // Check if dismissed
  const isDismissed = localStorage.getItem('pwa-install-prompt-dismissed') === 'true';
  
  // Check visit count
  let visitCount = parseInt(localStorage.getItem('pwa-visit-count') || '0');
  visitCount++;
  localStorage.setItem('pwa-visit-count', visitCount.toString());
  
  // Show prompt on 2nd visit, if mobile, not installed, and not dismissed
  if (isMobile && !isInstalled && !isDismissed && visitCount >= 2) {
    // Dispatch custom event to show install banner
    window.dispatchEvent(new CustomEvent('show-install-prompt'));
  }
});

// Export for component use
(window as any).showInstallPrompt = () => {
  if (deferredPrompt) {
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then((choiceResult: any) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      }
      deferredPrompt = null;
    });
  }
};

(window as any).dismissInstallPrompt = () => {
  localStorage.setItem('pwa-install-prompt-dismissed', 'true');
  window.dispatchEvent(new CustomEvent('hide-install-prompt'));
};
